import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoReorderThreeOutline, IoClose } from "react-icons/io5";
import useToogle from "../../../../hooks/useToggle";
import { ReactionPanel, SessionPlanPanel } from "../panels";
import StandardQueriesPanel from "components/panels/StandardQueries";
import RoomClient from "components/containers/utils/mediaSoup/RoomClient";
import ShareScreenControl from "./ShareScreen";
import { BadgeControl } from ".";
import ParticipantPanel from "../../../panels/Participant";
import NotePadApplication from "components/notepad";
import ChatApplication from "widgets/chat-application";
import Whiteboard from "widgets/white-board/WhiteBoard";
import { motion, AnimatePresence } from "framer-motion";
import { FaUserGroup, FaSquare } from "react-icons/fa6";
import { RxChatBubble } from "react-icons/rx";
import { BiNotepad } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { socketMessageType } from "types/Constants";
import { validateAndCreateSocketIfExist } from "utils/SocketValidator";
import { socketContextType } from "index";
import { useContext } from "react";
import SocketContext from "socket-context";
import { UserInfoMapperSocketHandler } from "utils/UserInfoMapper";
import axiosInstance from "config/axios";
import { RootState } from "store";
import { clearNewBadgeNotification, updateLiveStreamSettings } from "store/reducers/livestreamSettings";
import { raiseQuery, unraiseQuery, showQueryPopup } from "store/reducers/livestreamSettings";
import CLASSNAME from "./classname";

interface SliderPorps {
  roomClient: RoomClient | null;
}

const Slider = ({ roomClient }: SliderPorps) => {
  const settings: any = useSelector((state: RootState) => state.live.settings);
  const { activeQueries } = useSelector((state: RootState) => state.live.settings);
  const [isMenuOn, toggleMenu] = useToogle();
  const [visible, setVisible] = useState(false);
  const [translateY, setTranslateY] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startTranslateY = useRef(0);
  const drawerRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLDivElement>(null);
  const [isQueriesOpen, setIsQueriesOpen] = useState(false);
  const [isBadgesOpen, setIsBadgesOpen] = useState(false);
  const clickInProgress = useRef(false);
  const dispatch = useDispatch();

  // Use ref to track timeouts for raise hand
  const raiseHandTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [activeSubview, setActiveSubview] = useState<
    "chat" | "notepad" | "whiteboard" | "participant" | "sessionPlan" | "badges" | null
  >(null);

  const userData = useSelector((state: any) => state.auth.user);
  let userInfoHandler = new UserInfoMapperSocketHandler();
  const socketList: socketContextType[] = useContext(SocketContext);

  // Check if hand is currently raised
  const isHandRaised = !!activeQueries && activeQueries["raise_hand"];

  const openDrawer = useCallback(() => {
    if (clickInProgress.current) return;
    clickInProgress.current = true;
    if (!isMenuOn) toggleMenu();
    setVisible(true);
    setTranslateY(0);
    setTimeout(() => (clickInProgress.current = false), 300);
  }, [isMenuOn, toggleMenu]);

  const closeDrawer = useCallback(() => {
    if (clickInProgress.current) return;
    clickInProgress.current = true;
    if (isMenuOn) toggleMenu();
    setActiveSubview(null);
    setTranslateY(100);
    setTimeout(() => {
      setVisible(false);
      clickInProgress.current = false;
    }, 300);
  }, [isMenuOn, toggleMenu]);

  useEffect(() => {
    if (isMenuOn && !visible) openDrawer();
    else if (!isMenuOn && visible) closeDrawer();
  }, [isMenuOn, openDrawer, closeDrawer, visible]);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (hamburgerRef.current?.contains(target) || drawerRef.current?.contains(target)) return;
      closeDrawer();
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [visible, closeDrawer]);

  const handleHamburgerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (clickInProgress.current) return;
    if (isMenuOn) closeDrawer();
    else openDrawer();
  };

  const handleDragStart = (clientY: number, e: React.TouchEvent | React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Ignore drag if the touch starts on a button or interactive element
    if (target.closest('button') || target.closest('input') || target.closest('[data-badge-trigger]')) {
      return;
    }
    if (!visible || !isMenuOn) return;
    setIsDragging(true);
    startY.current = clientY;
    startTranslateY.current = translateY;
    e.stopPropagation();
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging) return;
    const deltaY = clientY - startY.current;
    const drawerHeight = window.innerHeight * 0.55;
    const deltaPercent = (deltaY / drawerHeight) * 100;
    const newTranslate = Math.max(0, Math.min(100, startTranslateY.current + deltaPercent));
    setTranslateY(newTranslate);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (translateY > 30) closeDrawer();
    else setTranslateY(0);
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => handleDragMove(e.clientY);
    const onMouseUp = () => handleDragEnd();
    if (isDragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging, translateY, handleDragMove, handleDragEnd]);

  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      handleDragMove(e.touches[0].clientY);
      e.preventDefault();
    };
    const onTouchEnd = () => handleDragEnd();
    if (isDragging) {
      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
    }
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [isDragging, translateY, handleDragMove, handleDragEnd]);

  // useEffect(() => {
  //   if (isBadgesOpen) {
  //     const timeout = setTimeout(() => {
  //       const badgeBtn = document.querySelector('[data-badge-trigger]') as HTMLElement;
  //       if (badgeBtn) badgeBtn.click();
  //     }, 50);
  //     return () => clearTimeout(timeout);
  //   }
  // }, [isBadgesOpen]);

  const saveActions = async (subType: string, isLowerHand: boolean = false) => {
    const session = await axiosInstance.post(
      "/trainee/live_session_plan_preview_trainee",
      { session_map_id: settings.roomId }
    );

    const responseData = session?.data?.responseData || {};
    const allActivities = [
      ...(responseData.prePlannedActivities || []),
      ...(responseData.quickSetupActivities || []),
    ];

    const launchData = allActivities.find((el: any) => el.status === "LAUNCH");
    if (!launchData?.id) {
      return;
    }

    const engagementType = subType === "Raise a Hand" ? "L_RAISE_HAND" : "L_STANDARD_QUERIES";

    const obj = {
      engagementType,
      userId: userData?.id,
      sessionActivityExecutionLogId: launchData?.id,
      isRead: "NO",
      participantId: userData?.id,
      subType: subType === "Raise a Hand" ? (isLowerHand ? "LOWER_HAND" : "RAISE_HAND") : null,
    };

    await axiosInstance.post("/session/save_session_actions_log", obj);
  };

  const sendRaiseHand = async (query: string, isLowerHand: boolean = false) => {
    const isRaiseHand = query === "Raise a Hand";

    const formattedTime = new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    let reactionType: string;
    let socketType: string;

    if (isLowerHand) {
      reactionType = "lower_hand";
      socketType = socketMessageType?.LOWER_HAND;
    } else if (isRaiseHand) {
      reactionType = "raise_hand";
      socketType = socketMessageType?.RAISE_HAND;
    } else {
      reactionType = "std_query";
      socketType = socketMessageType?.RAISE_HAND;
    }

    userInfoHandler = validateAndCreateSocketIfExist(settings.roomId, () => { }, socketList);

    userInfoHandler.sendMessage(
      JSON.stringify({
        type: socketType,
        user_reaction_data: {
          reaction_type: reactionType,
          reacted_by_email: userData?.email,
          reacted_by_name: userData?.name,
          reacted_by_type: "trainee",
          query_message: query,
          time: formattedTime,
        },
        room_id: settings.roomId,
      })
    );

    await axiosInstance.post("/session/create_session_raised_hand", {
      session_map_id: Number(settings.roomId),
      content: JSON.stringify({
        reaction_type: reactionType,
        reacted_by_email: userData?.email,
        reacted_by_name: userData?.name,
        reacted_by_type: "trainee",
        query_message: reactionType === "std_query" ? query : null,
        time: formattedTime,
      }),
      trainee_id: userData?.id,
      type: reactionType,
    });
  };

  const handleRaiseHand = async () => {
    if (isHandRaised) {
      // If hand is already raised, lower it
      await handleLowerHand();
    } else {
      // Raise hand
      const query = "Raise a Hand";
      const queryType = "raise_hand";

      // // Clear any existing timeout
      // if (raiseHandTimeoutRef.current) {
      //   clearTimeout(raiseHandTimeoutRef.current);
      //   raiseHandTimeoutRef.current = null;
      // }

      // // Create timeout for automatic lower after 20 seconds
      // const timeoutId = setTimeout(() => {
      //   console.log("Auto-lowering hand after 20 seconds");
      //   handleLowerHand(true);
      // }, 20000);

      // // Store timeout in ref
      // raiseHandTimeoutRef.current = timeoutId;

      // Store the query in Redux with timeout ID
      dispatch(raiseQuery({ queryType, timeoutId: null }));

      // Show popup for raise hand
      // dispatch(showQueryPopup({ queryType, originalQuery: query }));

      // Send raise hand socket message
      await sendRaiseHand(query, false);

      // Save actions
      await saveActions(query, false);

      console.log("Hand raised with 20-second auto-lower");
    }
  };

  const handleLowerHand = async (isAutoUnraise: boolean = false) => {
    const queryType = "raise_hand";
    const query = "Raise a Hand";

    // Clear timeout if it exists
    if (raiseHandTimeoutRef.current) {
      clearTimeout(raiseHandTimeoutRef.current);
      raiseHandTimeoutRef.current = null;
    }

    // Send lower hand socket message
    await sendRaiseHand(query, true);

    // Save actions for lower hand
    await saveActions(query, true);

    // Remove from Redux store
    dispatch(unraiseQuery(queryType));

    console.log(`Hand lowered, auto: ${isAutoUnraise}`);
  };

  const handleQuerySelect = (query: string) => {
    console.log("Query selected:", query);
    setIsQueriesOpen(false);
    dispatch(updateLiveStreamSettings({ raiseHand: false }));
  };

  const tabs = [
    { id: "participant", icon: <FaUserGroup size={20} /> },
    { id: "whiteboard", icon: <FaSquare size={20} /> },
    { id: "chat", icon: <RxChatBubble size={20} /> },
    { id: "notepad", icon: <BiNotepad size={20} /> },
  ];

  const dragBarRef = useRef<HTMLDivElement>(null);

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (raiseHandTimeoutRef.current) {
        clearTimeout(raiseHandTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative z-50 laptop:hidden desktop:hidden largescreen:hidden tablet:hidden">
      {/* Hamburger Button */}
      <div
        ref={hamburgerRef}
        className={`${CLASSNAME}`}
        style={{
          background: "rgb(95, 72, 138)",
          boxShadow:
            "rgb(152, 122, 209) -8px -8px 16px inset, rgba(2, 2, 2, 0.61) 8px 8px 8px inset",
        }}
        onClick={handleHamburgerClick}
        onTouchEnd={(e) => {
          e.preventDefault();
          handleHamburgerClick(e as any);
        }}
      >
        <IoReorderThreeOutline color="white" size="6vw" style={{ maxWidth: 30 }} />
        {settings.hasNewBadge && (
          <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full" />
        )}
      </div>

      {visible && (
        <div
          ref={drawerRef}
          className="fixed bottom-0 left-1/2 w-[95vw] h-[55vh] bg-[#1e1e24] z-50 rounded-t-2xl transform -translate-x-1/2 overflow-hidden"
          style={{
            transform: `translate(-50%, ${translateY}%)`,
            transition: isDragging ? "none" : "transform 300ms ease-out",
            touchAction: "none",
          }}
          onMouseDown={(e) => { if (e.button === 0) handleDragStart(e.clientY, e); }}
          onTouchStart={(e) => { handleDragStart(e.touches[0].clientY, e); }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag Bar */}
          <div
            ref={dragBarRef}
            className="flex justify-center pt-2"
            onMouseDown={(e) => { if (e.button === 0) handleDragStart(e.clientY, e); }}
            onTouchStart={(e) => { handleDragStart(e.touches[0].clientY, e); }}
          >
            <div className="w-[15%] h-[2px] bg-gray-500 rounded-full" />
          </div>

          {/* Emoji Row */}
          <div className="w-full px-4 mt-2">
            <div className="flex items-center justify-between py-1 px-2 rounded-full bg-[#1d1e23]  overflow-hidden">
              <ReactionPanel />
            </div>
          </div>

          {/* 3-Column Grid Layout */}
          <div className="grid grid-cols-[40%,40%,20%] gap-2 px-4 mt-4">
            {/* First Column: QUERIES and SESSION PLAN */}
            <div className="flex flex-col gap-2">
              <div
                className="flex items-center justify-center w-full h-12 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase cursor-pointer"

                onClick={() => {
                  setIsQueriesOpen((prev) => !prev)
                }}
              >
                QUERIES
                {isQueriesOpen && (
                  <div
                    className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-2"
                    onClick={() => setIsQueriesOpen(false)}
                  >
                    <div
                      className="w-full max-h-full flex flex-col gap-2 p-2 bg-[#1e1e24] rounded-lg overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end">
                        <button
                          onClick={() => setIsQueriesOpen(false)}
                          className="p-1 rounded-full hover:bg-gray-700"
                        >
                          <IoClose size="12px" className="text-white" />
                        </button>
                      </div>
                      <StandardQueriesPanel onSelect={handleQuerySelect} />
                    </div>
                  </div>
                )}
              </div>
              <div
                className="flex items-center justify-center w-full h-12 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase cursor-pointer"

                onClick={() => setActiveSubview("sessionPlan")}
              >
                SESSION PLAN
              </div>
            </div>

            {/* Second Column: BADGES and VISITING CARDS */}
            {/* <div className="flex flex-col gap-2">
              <div
                className="flex items-center justify-center w-full h-12 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase cursor-pointer"

                onClick={() => {
                  const badgeBtn = document.querySelector(".badge-control-btn") as HTMLElement;
                  badgeBtn?.click();
                }}
              >
                <div className="badge-control-btn transform scale-50">
                  <BadgeControl showButton={true} />
                </div>
                <span className="ml-2">BADGES</span>
              </div>
              <div
                className="flex items-center justify-center w-full h-12 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase"

              >
                VISITING CARDS
              </div>
            </div> */}
            <div className="flex flex-col gap-2">
              <div
                className="flex items-center justify-center relative w-full h-12 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase cursor-pointer"
                onClick={() => {
                  setActiveSubview("badges");
                  if (settings.hasNewBadge) {
                    dispatch(clearNewBadgeNotification());
                  }
                }}
              >
                {settings.hasNewBadge && (
                  <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full" />
                )} 
                <span className="ml-2">BADGES</span>
              </div>
              <div className="flex items-center justify-center w-full h-12 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase">
                VISITING CARDS
              </div>
            </div>

            {/* Third Column: RAISE/LOWER HAND - Single Toggle Button */}
            <div className="flex items-start justify-center">
              <div
                className={`w-full h-[120px] rounded-full flex items-center justify-center text-2xl cursor-pointer transition-all duration-300 ${isHandRaised
                  ? "bg-[#634c8f] shadow-[0_0_20px_rgba(99,76,143,0.5)] border-2 border-[#e8bd46]"
                  : "bg-[#2a2d34] shadow-[-2px_-2px_4px_rgba(255,255,255,0.25),_2px_2px_4px_rgba(0,0,0,0.25)] border border-[#634c8f]"
                  }`}
                onClick={handleRaiseHand}
              >
                <div className={`transition-all duration-300 ${isHandRaised ? "scale-110 text-[#e8bd46]" : "scale-100 text-white"}`}>
                  {isHandRaised ? "✋" : "✋"}
                </div>
                {/* Active indicator */}
                {/* {isHandRaised && (
                  <div className="absolute top-2 right-2 w-3 h-3 bg-[#e8bd46] rounded-full animate-ping" />
                )} */}
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="flex justify-between items-center mt-2 px-4">
            {/* Share Screen Button */}
            <div
              className="flex items-center justify-center py-1 px-3 rounded-full bg-[#2a2d34] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)] text-white font-bold text-xs uppercase cursor-pointer"

              onClick={() => {
                const btn = document.querySelector(".share-screen-btn") as HTMLElement;
                btn?.click();
              }}
            >
              <div className="share-screen-btn transform scale-50">
                <ShareScreenControl roomClient={roomClient} />
              </div>
              <span className="ml-2">SHARE SCREEN</span>
            </div>

            {/* Icon Buttons */}
            <div className="flex gap-2">
              {tabs.map(({ id, icon }) => {
                const isActive = activeSubview === id;
                return (
                  <button
                    key={id}
                    onClick={() =>
                      setActiveSubview((prev) =>
                        prev === id ? null : (id as "participant" | "chat" | "notepad" | "whiteboard")
                      )
                    }
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? "bg-purple-600" : "bg-[#2a2d34] hover:bg-[#333]"} shadow-[inset_0_-2px_4px_rgba(0,0,0,0.5),0_2px_4px_rgba(255,255,255,0.1)]`}
                  >
                    {React.cloneElement(icon, {
                      className: isActive
                        ? "w-[60%] h-[60%] text-yellow-400"
                        : "w-[60%] h-[60%] text-white",
                    })}
                  </button>
                );
              })}
            </div>
          </div>

          <AnimatePresence>
            {activeSubview && (
              <motion.div
                key={activeSubview}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
                onClick={(e) => {
                  setActiveSubview(null);
                  if (isMenuOn) closeDrawer();
                }}
              >
                <div
                  className="bg-[#1e1e24] rounded-xl w-full h-full max-w-[1200px] max-h-[95vh] overflow-auto relative shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setActiveSubview(null)}
                    className="absolute top-2 right-2 p-2 rounded-full hover:bg-gray-700 transition z-[101]"
                  >
                    <IoClose size={24} className="text-white" />
                  </button>

                  {activeSubview === "chat" && <ChatApplication />}
                  {activeSubview === "whiteboard" && <Whiteboard />}
                  {activeSubview === "notepad" && <NotePadApplication />}
                  {activeSubview === "participant" && <ParticipantPanel />}
                  {activeSubview === "sessionPlan" && (
                    <div className="p-4">
                      <SessionPlanPanel />
                    </div>
                  )}
                  {activeSubview === "badges" && (
                    <div className="p-4">
                      <BadgeControl showButton={false} inDrawer={true} />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default Slider;